import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  MenuItem,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AppButton from "../../components/app-button/AppButton";
import TestPrint from "./TestPrint";

export default function PrinterSettings() {
  const [settings, setSettings] = useState({
    // General
    printer: "default",
    paperSize: "80mm",
    orientation: "portrait",
    copies: 1,

    // Invoice
    showLogo: true,
    showGST: true,
    showAddress: true,
    showCashier: false,
    showTableNo: true,
    footerMessage: "Thank you for visiting!",

    // KOT
    enableKOT: true,
    printWithoutPrices: true,
    printNotes: true,

    // Advanced
    language: "English",
    printQRCode: false,
    connectionType: "USB",
    margins: "5",
    fontSize: "Medium",
  });

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleTestPrint = () => {
    console.log("🖨️ Test Print initiated with settings:", settings);
    alert("Sending test print to " + settings.printer);
  };

  const handleSave = () => {
    console.log("✅ Settings saved:", settings);
    alert("Printer settings saved!");
  };

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Stack spacing={2}>{children}</Stack>
      <Divider sx={{ my: 2 }} />
    </Box>
  );

  const Toggle = ({ label, checked, onChange }) => (
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={label}
    />
  );

  const Dropdown = ({ label, value, options, isDisabled, onChange }) => (
    <TextField
      select
      label={label}
      value={value}
      disabled={isDisabled}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <Card sx={{ mb: 3, p: 2, borderRadius: 3 }}>
        <CardContent>
          <Box>
            <Section title="General Printing">
              <Dropdown
                label="Select Printer"
                value={settings.printer}
                isDisabled={true}
                options={[
                  { value: "default", label: "Default System Printer" },
                  { value: "thermal", label: "Thermal Printer" },
                  { value: "network", label: "Network Printer" },
                ]}
                onChange={(v) => handleChange("printer", v)}
              />

              <Dropdown
                label="Paper Size"
                value={settings.paperSize}
                isDisabled={true}
                options={[
                  { value: "58mm", label: "58mm" },
                  { value: "80mm", label: "80mm" },
                  { value: "A4", label: "A4" },
                ]}
                onChange={(v) => handleChange("paperSize", v)}
              />
              <Dropdown
                label="Orientation"
                value={settings.orientation}
                isDisabled={true}
                options={[
                  { value: "portrait", label: "Portrait" },
                  { value: "landscape", label: "Landscape" },
                ]}
                onChange={(v) => handleChange("orientation", v)}
              />
            </Section>

            {/* Invoice */}
            <Section title="Invoice Settings">
              <Toggle
                label="Print Business Logo"
                checked={settings.showLogo}
                onChange={(v) => handleChange("showLogo", v)}
              />
              <Toggle
                label="Print GST Number/ Tax Details"
                checked={settings.showGST}
                onChange={(v) => handleChange("showGST", v)}
              />
              <Toggle
                label="Print Store Address"
                checked={settings.showAddress}
                onChange={(v) => handleChange("showAddress", v)}
              />
              <Toggle
                label="Print Store Contact Number"
                checked={settings.showAddress}
                onChange={(v) => handleChange("showAddress", v)}
              />
              <Toggle
                label="Print Cashier/Waiter Name"
                checked={settings.showCashier}
                onChange={(v) => handleChange("showCashier", v)}
              />
              <Toggle
                label="Print Table Number / Order Type"
                checked={settings.showTableNo}
                onChange={(v) => handleChange("showTableNo", v)}
              />
              <TextField
                label="Footer Message"
                value={settings.footerMessage}
                onChange={(e) => handleChange("footerMessage", e.target.value)}
                fullWidth
              />
            </Section>

            {/* KOT */}
            <Section title="KOT (Kitchen Order Ticket) Settings">
              <Toggle
                label="Enable KOT Printing"
                checked={settings.enableKOT}
                onChange={(v) => handleChange("enableKOT", v)}
              />
              <Toggle
                label="Print Items Without Prices"
                checked={settings.printWithoutPrices}
                onChange={(v) => handleChange("printWithoutPrices", v)}
              />
              <Toggle
                label="Print Customer Notes"
                checked={settings.printNotes}
                onChange={(v) => handleChange("printNotes", v)}
              />
              <Box>
                <TextField
                  type="number"
                  label="Number of KOT Copies"
                  value={settings.copies}
                  onChange={(e) => handleChange("copies", e.target.value)}
                  fullWidth
                  helperText="Set how many copies of the Kitchen Order Ticket (KOT) should be printed for each order."
                />
              </Box>
            </Section>

            {/* Advanced */}
            <Section title="Advanced Options">
              <Dropdown
                label="Print Language"
                isDisabled={true}
                value={settings.language}
                options={[
                  { value: "English", label: "English" },
                  { value: "Spanish", label: "Spanish" },
                  { value: "French", label: "French" },
                ]}
                onChange={(v) => handleChange("language", v)}
              />
              <Dropdown
                isDisabled={true}
                label="Connection Type"
                value={settings.connectionType}
                options={[
                  { value: "USB", label: "USB / Bluetooth" },
                  { value: "Network", label: "Network" },
                  { value: "Bluetooth", label: "Bluetooth" },
                ]}
                onChange={(v) => handleChange("connectionType", v)}
              />
              <TextField
                disabled={true}
                label="Margins (mm)"
                value={settings.margins}
                onChange={(e) => handleChange("margins", e.target.value)}
                fullWidth
              />
              <Dropdown
                isDisabled={true}
                label="Font Size"
                value={settings.fontSize}
                options={[
                  { value: "Small", label: "Small" },
                  { value: "Medium", label: "Medium" },
                  { value: "Large", label: "Large" },
                ]}
                onChange={(v) => handleChange("fontSize", v)}
              />

              <Toggle
                label="Print UPI Payment QR"
                checked={settings.printQRCode}
                onChange={(v) => handleChange("printQRCode", v)}
              />

              <TextField
                label="Enter VPA for QR Code"
                placeholder="example@upi"
                value={null}
                onChange={(e) => handleChange("margins", e.target.value)}
                fullWidth
              />
            </Section>

            {/* Save Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                gap: 2,
              }}
            >
              <TestPrint />
              <AppButton variant="contained" onClick={handleSave}>
                Save Settings
              </AppButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
